import React, { useState } from 'react';
import { RIDE_STATUS } from './useUberRides';
import UberSubmitErrorModal from './UberSubmitErrorModal';
import UberRideInfoModal from './UberRideInfoModal';

function formatPickupDisplay(ride) {
  if (ride.is_flexible_pickup) return 'Flexible';

  if (ride.pickup_date && ride.pickup_time) {
    const time = ride.pickup_time.slice(0, 5);
    return `${ride.pickup_date} ${time}`;
  }

  if (ride.pickup_date) return ride.pickup_date;

  return '–';
}

function formatLocation(loc) {
  if (!loc) return '–';

  switch (loc.type) {
    case 'office':
      return `Office: ${loc.label}`;
    case 'site':
      return `Site: ${loc.label}`;
    case 'custom':
      return `Address: ${loc.label}`;
    default:
      return '–';
  }
}

function renderSeatLine(seat) {
  return (
      <div className="uber-seat-row">
        <strong>Seat {seat.seat_number}</strong>
        {' – '}
        {seat.canvasser_name ? seat.canvasser_name : <span className="ghost">Unassigned</span>}
      </div>
  );
}

function renderPrimaryRider(ride) {
  if (!ride.seats || !ride.seats.length) {
    return <span className="ghost">–</span>;
  }

  if (typeof ride.seats[0] === 'number') {
    return <span className="ghost">–</span>;
  }

  const primary = ride.seats.find((s) => s.is_primary_rider);

  if (!primary) {
    return <span className="ghost">–</span>;
  }

  return renderSeatLine(primary);
}

function renderAdditionalRiders(ride) {
  if (!ride.seats || !ride.seats.length) {
    return <span className="ghost">–</span>;
  }

  if (typeof ride.seats[0] === 'number') {
    return <span className="ghost">–</span>;
  }

  const additional = ride.seats.filter((s) => !s.is_primary_rider);

  if (!additional.length) {
    return <span className="ghost">–</span>;
  }

  return (
      <div className="uber-seat-list">
        {additional.map((seat) => (
            <div key={seat.seat_number}>{renderSeatLine(seat)}</div>
        ))}
      </div>
  );
}

function formatStatus(status) {
  if (!status) return '';
  return status.replace(/_/g, ' ');
}

export default function UberRideTable({ rides, loadingRideIds, onCancelRide, onSubmitToUber, infoModal, onFetchRideInfo, onCloseRideInfo }) {
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const data = rides || [];

  const handleSubmitToUber = async (rideId) => {
    try {
      await onSubmitToUber(rideId);
    } catch (error) {
      // Extract error message from various error formats
      const message = error?.response?.data?.message
          || error?.message
          || 'An unexpected error occurred while submitting the ride to Uber.';

      setErrorMessage(message);
      setShowErrorModal(true);
    }
  };

  const handleCloseErrorModal = () => {
    setShowErrorModal(false);
    setErrorMessage('');
  };

  if (!data.length) {
    return <div className="uber-rides-empty">No Uber rides yet for this crew.</div>;
  }

  return (
      <>
        <table className="uber-rides-table">
          <thead>
          <tr>
            <th>Pickup Time</th>
            <th>Pickup</th>
            <th>Dropoff</th>
            <th>Primary Rider</th>
            <th>Additional Riders</th>
            <th>Status</th>
            <th style={{ textAlign: 'center' }}>Actions</th>
          </tr>
          </thead>
          <tbody>
          {data.map((ride) => {
            // Check if this specific ride is loading
            const isLoadingThisRide = loadingRideIds ? loadingRideIds.has(ride.id) : ride.isLoading;

            // Determine if cancel should be disabled based on status
            const cannotCancel = [
              // RIDE_STATUS.UBER_ACTIVE,
              RIDE_STATUS.UBER_CANCELED,
              RIDE_STATUS.CANCELED,
              RIDE_STATUS.RIDER_CANCELED,
              RIDE_STATUS.UBER_COMPLETED
            ].includes(ride.status);

            return (
                <tr key={ride.id} data-record-id={ride.id}>
                  <td>{formatPickupDisplay(ride)}</td>
                  <td>{formatLocation(ride.pickup)}</td>
                  <td>{formatLocation(ride.dropoff)}</td>
                  <td>{renderPrimaryRider(ride)}</td>
                  <td>{renderAdditionalRiders(ride)}</td>
                  <td>
                    <div>
                  <span className={`uber-status-badge uber-status-${ride.status}`}>
                    {formatStatus(ride.status)}
                  </span>
                      {ride.readyForUber && (
                          <div
                              style={{
                                marginTop: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                              }}
                          >
                      <span
                          style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            backgroundColor: '#28a745',
                            display: 'inline-block',
                          }}
                      />
                            <span
                                style={{
                                  color: '#28a745',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                }}
                            >
                        Ready
                      </span>
                          </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                    >
                      <button
                          onClick={() => onFetchRideInfo(ride.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: '#0d6efd',
                            padding: '4px 8px',
                          }}
                          title="View ride info"
                      >
                        <i className="fa fa-info-circle" style={{ fontSize: '14px' }}></i>
                      </button>

                      <button
                          onClick={() => handleSubmitToUber(ride.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: ride.readyForUber ? 'pointer' : 'not-allowed',
                            color: ride.readyForUber ? '#28a745' : '#ccc',
                            padding: '4px 8px',
                            opacity: ride.readyForUber ? 1 : 0.5,
                          }}
                          disabled={!ride.readyForUber || isLoadingThisRide}
                          title={ride.readyForUber ? 'Ready to submit to Uber' : 'Not ready to submit'}
                      >
                        {isLoadingThisRide ? (
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '14px' }}></i>
                        ) : (
                            <i className="fa fa-paper-plane" style={{ fontSize: '14px' }}></i>
                        )}
                      </button>

                      <button
                          onClick={() => onCancelRide(ride.id)}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: cannotCancel ? 'not-allowed' : 'pointer',
                            color: cannotCancel ? '#ccc' : '#dc3545',
                            padding: '4px 8px',
                            opacity: cannotCancel ? 0.5 : 1,
                          }}
                          disabled={isLoadingThisRide || cannotCancel}
                          title={cannotCancel ? 'Cannot cancel this ride' : 'Cancel ride'}
                      >
                        {isLoadingThisRide ? (
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: '14px' }}></i>
                        ) : (
                            <i className="fa fa-times-circle" style={{ fontSize: '14px' }}></i>
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
            );
          })}
          </tbody>
        </table>

        <UberSubmitErrorModal
            show={showErrorModal}
            errorMessage={errorMessage}
            onClose={handleCloseErrorModal}
        />

        <UberRideInfoModal
            show={infoModal?.show}
            rideInfo={infoModal?.data}
            loading={infoModal?.loading}
            onClose={onCloseRideInfo}
        />
      </>
  );
}